import { useEffect, useState } from "react";
import { UI, FONT } from "../constants/tokens";
import { fmtPct, shownDir } from "../utils/formatters";
import { fetchLatestMarketSummary, isFromToday, updatedLabel } from "../utils/marketSummary";
import { israelClock, useMarketStatus, staleDate } from "../utils/marketStatus";
import EmptyState from "./EmptyState";
import Icon from "./Icon";
import LTR from "./LTR";
import { Skeleton as Skel, Loading } from "./Skeleton";

// ─── "מה קורה בשוק היום" (sprint 18, stage 2) ──────────────────────────────
// One summary for every user: what named outlets reported, three or four
// lines, each with a link to the article it is based on. The text is a
// model's reading of headlines and is labelled as one; the index row above it
// is live data, never the model's (liveIndices, below). No summary
// from today → the latest one, with its date. None at all → the empty state.

// Sprint 19, stage 4: S&P 500, Nasdaq and VIX · the Dow is on the ticker
// tape. Before the open the two futures take the indices' place (FuturesRow).
const INDEX_ROWS = [
  { key: "sp500",  name: "S&P 500" },
  { key: "nasdaq", name: "Nasdaq" },
  { key: "vix",    name: "VIX", value: true },
];

const tone = v => (shownDir(v) > 0 ? UI.gain : shownDir(v) < 0 ? UI.loss : UI.text.primary);

// One session per row (stage 2 review): the morning run is before the open,
// so its quotes are the previous session's close, and VIX can carry another
// stamp than the three indices. Only the quotes from the newest New York
// session are shown, and the row says which moment they are from.
const nyDate = ts => new Date(ts).toLocaleDateString("en-CA", { timeZone: "America/New_York" });
function sessionQuotes(indices) {
  const rows = INDEX_ROWS.filter(r => {
    const q = indices?.[r.key];
    return q && Number.isFinite(q.change_pct) && q.quote_ts && Number.isFinite(new Date(q.quote_ts).getTime());
  });
  if (!rows.length) return { rows: [] };
  const newest = rows.map(r => nyDate(indices[r.key].quote_ts)).sort().pop();
  const kept = rows.filter(r => nyDate(indices[r.key].quote_ts) === newest);
  const latest = kept.map(r => indices[r.key].quote_ts).sort().pop();
  return { rows: kept, latest };
}

// Named by the New York session the quote belongs to: an index's final
// stamp can land after midnight in Israel (Nasdaq ~00:15), and "Saturday"
// would be the wrong day for Friday's close.
function sessionMoment(ts) {
  const session = new Date(`${nyDate(ts)}T12:00:00Z`).toLocaleDateString("he-IL", { weekday: "long", day: "numeric", month: "numeric", timeZone: "UTC" });
  const nyMin = (() => { const [h, m] = new Date(ts).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: "America/New_York" }).split(":"); return Number(h) * 60 + Number(m); })();
  return nyMin >= 16 * 60
    ? `סגירת ${session}`
    : `${session} · ${new Date(ts).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Jerusalem" })} שעון ישראל`;
}

// "כל המדדים ←" (26/10/2026): to the "שווקים" card on the portfolio page.
function AllIndices({ onClick }) {
  return (
    <button onClick={onClick}
      style={{ background: "transparent", border: "none", padding: "0 2px", minHeight: 32, fontSize: 13, fontWeight: 700, color: UI.interactive, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
      כל המדדים ←
    </button>
  );
}

function IndexRow({ indices, onAll }) {
  const { rows, latest } = sessionQuotes(indices);
  if (!rows.length) return null;
  const sources = [...new Set(rows.map(r => indices[r.key].source).filter(Boolean))];
  const at = sessionMoment(latest);
  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px" }}>
        {rows.map(r => {
          const q = indices[r.key];
          return (
            <LTR key={r.key} className="si-num" style={{ display: "inline-flex", alignItems: "baseline", gap: 5, fontSize: 13.5, whiteSpace: "nowrap" }}>
              <span style={{ fontFamily: FONT.latin, fontWeight: 700, color: UI.text.primary }}>{r.name}</span>
              {r.value && <span style={{ color: UI.text.secondary }}>{Number(q.value).toFixed(2)}</span>}
              <span style={{ fontWeight: 700, color: tone(q.change_pct) }}>{fmtPct(q.change_pct)}</span>
            </LTR>
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
        <div style={{ fontSize: 11.5, color: UI.text.muted, lineHeight: 1.6 }}>
          {at} · שינוי מול הסגירה הקודמת{sources.length > 0 && <> · <LTR>{sources.join(", ")}</LTR></>}
        </div>
        {onAll && <AllIndices onClick={onAll}/>}
      </div>
    </div>
  );
}

// ─── Before the open: the futures and VIX (sprint 19, stage 4) ──────────────
// The S&P 500 and Nasdaq futures from Yahoo (extended-hours.js), each only
// when its last trade is from the last 30 minutes (utils/extendedHours.js).
// VIX beside them from the stored run, with the session its quote is from.
// No fresh future → null, and the card shows the index row as on any day.
function FuturesRow({ sp, nq, vix }) {
  const futs = [{ name: "S&P 500", q: sp }, { name: "Nasdaq", q: nq }].filter(f => f.q);
  if (!futs.length) return null;
  const latest = futs.map(f => f.q.time).sort().pop();
  const vixOk = vix && Number.isFinite(vix.change_pct) && vix.quote_ts;
  return (
    <div data-futures>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px" }}>
        {futs.map(f => (
          <span key={f.name} style={{ display: "inline-flex", alignItems: "baseline", gap: 5, fontSize: 13.5, whiteSpace: "nowrap" }}>
            <span style={{ fontWeight: 700, color: UI.text.primary }}>חוזי <LTR>{f.name}</LTR></span>
            <LTR className="si-num" style={{ fontWeight: 700, color: tone(f.q.pct) }}>{fmtPct(f.q.pct)}</LTR>
          </span>
        ))}
        {vixOk && (
          <LTR className="si-num" style={{ display: "inline-flex", alignItems: "baseline", gap: 5, fontSize: 13.5, whiteSpace: "nowrap" }}>
            <span style={{ fontFamily: FONT.latin, fontWeight: 700, color: UI.text.primary }}>VIX</span>
            <span style={{ color: UI.text.secondary }}>{Number(vix.value).toFixed(2)}</span>
            <span style={{ fontWeight: 700, color: tone(vix.change_pct) }}>{fmtPct(vix.change_pct)}</span>
          </LTR>
        )}
      </div>
      <div style={{ fontSize: 11.5, color: UI.text.muted, marginTop: 4, lineHeight: 1.6 }}>
        לפני הפתיחה · חוזים <LTR className="si-num">{israelClock(latest)}</LTR> שעון ישראל, מול הסגירה הקודמת · <LTR>Yahoo Finance</LTR>
        {/* VIX is the stored run's: named by its session, as the index row
            names it, never by a bare time that reads as today's. */}
        {vixOk && <> · <LTR>VIX</LTR>: {sessionMoment(vix.quote_ts)}{vix.source && <> · <LTR>{vix.source}</LTR></>}</>}
      </div>
    </div>
  );
}

// The model's Hebrew with each Latin or numeric run isolated, so "Oracle",
// "S&P 500" or "−1.2%" inside a sentence keep their own order.
const RUN = /([A-Za-z0-9$€£%&][A-Za-z0-9$€£%&.,'’\-−+/]*(?:\s+[A-Za-z0-9$€£%&][A-Za-z0-9$€£%&.,'’\-−+/]*)*)/g;
export function Bidi({ text }) {
  // Punctuation that closes the Hebrew sentence stays outside the run, or a
  // sentence ending "… CoreWeave." would put its period on the wrong side.
  // A comma and a space between two Latin names ("לפי Yahoo, Microsoft") is
  // the Hebrew sentence's comma: each name is its own run (sprint 20).
  return String(text).split(RUN).map((part, i) => {
    if (!(i % 2)) return part;
    const [, run, tail] = /^(.*?)([.,'’]*)$/.exec(part);
    const pieces = run.split(/(,\s+)/);
    return <span key={i}>{pieces.map((p, k) => (k % 2 ? p : <LTR key={k}>{p}</LTR>))}{tail}</span>;
  });
}

// "Reuters ו-CNBC" · each name isolated, the Hebrew joiners outside it.
function Outlets({ names }) {
  const n = (names || []).filter(Boolean);
  return n.map((s, i) => (
    <span key={s}>{i === 0 ? "" : i === n.length - 1 ? " ו-" : ", "}<LTR>{s}</LTR></span>
  ));
}

// One reported line: the outlet as a chip above it (sprint 19, stage 4), what
// it reported, and its articles.
function SummaryLine({ line }) {
  const prefix = `${line.source}: `;
  const body = line.text.startsWith(prefix) ? line.text.slice(prefix.length) : line.text;
  const links = (line.articles || []).filter(a => /^https?:\/\//.test(a.url || ""));
  return (
    <li style={{ fontSize: 14.5, lineHeight: 1.65, color: UI.text.primary }}>
      <div style={{ marginBottom: 3 }}>
        <LTR style={{ display: "inline-block", fontFamily: FONT.latin, fontSize: 12, fontWeight: 700, color: UI.text.secondary, background: UI.fill.subtle, borderRadius: 999, padding: "1px 9px" }}>{line.source}</LTR>
      </div>
      <Bidi text={body}/>
      {links.map((a, i) => (
        <a key={a.id || a.url} href={a.url} target="_blank" rel="noopener noreferrer" title={a.headline}
          style={{ marginInlineStart: 6, fontSize: 12.5, fontWeight: 600, color: UI.interactive, textDecoration: "none", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 3 }}>
          {links.length > 1 ? <>לכתבה <LTR>{i + 1}</LTR></> : "לכתבה"}
          <Icon name="link" size={12} color={UI.interactive}/>
        </a>
      ))}
    </li>
  );
}

// The index row is LIVE (20/10/2026): the same quotes as the ticker tape
// (`live`, fetchMarketData · Yahoo with its own stamp), not the ones stored
// with the summary. The stored row was the moment of the run: at 18:00 the
// card still said VIX 18.72 from 16:02 while the tape said 17.85, and after
// the open the S&P and Nasdaq dropped out (their stored stamp was the
// previous session's, VIX's today's). The summary's text keeps its own time.
// A quote with no exchange stamp (a FRED close) is not in the row.
function liveIndices(live) {
  if (!live) return null;
  const out = {};
  for (const r of INDEX_ROWS) {
    const q = live[r.key];
    if (q && Number.isFinite(q.change_pct) && q.quote_ts) out[r.key] = { ...q, source: "Yahoo Finance" };
  }
  return out;
}

// `futures`: { sp, nq } from futureFor() · only before the open (HomeTab).
// `live`: fetchMarketData()'s answer · undefined while it loads.
export default function MarketTodayCard({ cardStyle, futures = null, live, onAllIndices }) {
  const market = useMarketStatus();
  const [row, setRow] = useState(undefined);   // undefined = loading · null = none
  // Read on mount, every ten minutes, and when the app comes back to the
  // front · a home screen left open picks up the 16:00 summary.
  useEffect(() => {
    let alive = true;
    const load = () => fetchLatestMarketSummary()
      .then(r => { if (alive) setRow(r); })
      .catch(() => { if (alive) setRow(prev => (prev === undefined ? null : prev)); });
    load();
    const id = setInterval(load, 10 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  if (row === undefined) {
    return (
      <div style={cardStyle}>
        <Loading><Skel w="70%" h={14} mb={12}/><Skel w="95%" h={14} mb={8}/><Skel w="85%" h={14} mb={8}/><Skel w="60%" h={14}/></Loading>
      </div>
    );
  }
  const lines = Array.isArray(row?.lines) ? row.lines.filter(l => l && l.source && l.text) : [];
  if (!row || !lines.length) {
    return (
      <div style={cardStyle}>
        <EmptyState icon="news" line="אין עדיין סיכום של חדשות השוק" sub="הסיכום נכתב פעמיים בימי מסחר, בסביבות 16:00 ו-23:30"/>
      </div>
    );
  }

  const today = isFromToday(row.created_at);
  // More than one trading session behind (22/10/2026): the summary says it
  // is not current, instead of passing as the last word on the market.
  const behind = row.session_date ? staleDate(row.session_date, market) : null;
  const fut = futures && (futures.sp || futures.nq)
    ? <FuturesRow sp={futures.sp} nq={futures.nq} vix={liveIndices(live)?.vix}/> : null;
  return (
    <div style={cardStyle} data-market-today>
      {/* When the summary was written, first (sprint 19, stage 4). */}
      {behind ? (
        <div data-market-today-stale role="status" style={{ fontSize: 12.5, fontWeight: 700, color: UI.caution, background: UI.cautionSoft, borderRadius: 8, padding: "6px 10px", marginBottom: 10, lineHeight: 1.5 }}>
          לא עודכן מאז <bdi>{updatedLabel(row.created_at)}</bdi> · <LTR className="si-num">{behind}</LTR> ימי מסחר בלי סיכום חדש
        </div>
      ) : (
        <div style={{ fontSize: 12.5, fontWeight: 700, color: UI.text.secondary, marginBottom: 10 }}>
          {today ? <>עודכן <LTR className="si-num">{updatedLabel(row.created_at)}</LTR></> : <>הסיכום האחרון · <bdi>{updatedLabel(row.created_at)}</bdi></>}
        </div>
      )}
      {fut ? <>{fut}{onAllIndices && <div style={{ textAlign: "end" }}><AllIndices onClick={onAllIndices}/></div>}</>
        : <IndexRow indices={liveIndices(live)} onAll={onAllIndices}/>}

      <ul style={{ listStyle: "none", margin: "14px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
        {lines.map((l, i) => <SummaryLine key={i} line={l}/>)}
      </ul>

      {/* The AI label, at the foot · gold is the brand's mark for AI (VISION.md). */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 14, fontSize: 12.5, color: UI.text.secondary }}>
        <Icon name="ai" size={14} color={UI.accent.base}/>
        <span>סיכום <LTR>AI</LTR> של כותרות מ-<Outlets names={row.sources?.length ? row.sources : [...new Set(lines.map(l => l.source))]}/> · ייתכנו אי-דיוקים</span>
      </div>
      <div style={{ fontSize: 11.5, color: UI.text.muted, marginTop: 6, lineHeight: 1.6 }}>
        חדשות מ-<LTR>Finnhub</LTR>, סיכום <LTR>Google Gemini</LTR> · מידע בלבד · אינו ייעוץ השקעות
      </div>
    </div>
  );
}
